import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { dateTimeFormatter } from "../../utils/dateTimeFormatter";
import FileRow from "./FileRow";
import { useClipboard } from "../../context/MoveCopyContext.js";
import SuccessPopup from "../shared/SuccessPopup.jsx";
import ConfirmPopup from "../shared/ConfirmPopup.jsx";
import { toast } from "react-toastify";
import { fetchAndDownloadFile } from "../utils/generalUtilities.js";
import FileCard from "./FileCard.jsx";
import userFileImage from "./../../assets/Images/user-file-image.png";
import userPdfImage from "./../../assets/Images/user-pdf-image.png";
import userExcelImage from "./../../assets/Images/user-excel-image.png";
import userDocxImage from "./../../assets/Images/user-docx-image.png";
import FilePreviewDialog from "./FilePreviewDialog"; // Import the FilePreviewDialog component
import renameIcon from '../../assets/Images/rename.png';
import copyIcon from '../../assets/Images/copy.png';
import moveIcon from '../../assets/Images/move.png';
import viewIcon from '../../assets/Images/view.png';
const FileList = ({
  files,
  refresh,
  docStorePermissions,
  activeTab,
  fetchStorageInfo,
  viewMode,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const wopiBaseUrl = process.env.REACT_APP_WOPI_BASE_URL;
  const accessToken = sessionStorage.getItem("accessToken");
  const headers = accessToken ? { Authorization: accessToken } : {};
  const [modalState, setModalState] = useState({
    isOpen: false,
    folder: null,
    newName: "",
    extension: "",
  });
  const { startMove, startCopy } = useClipboard();
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    file: null,
  });
  const contextMenuRef = useRef(null);
  const canView = docStorePermissions.includes("VIEW");
  const canRename = docStorePermissions.includes("RENAME");
  const canDelete = docStorePermissions.includes("DELETE");
  const canMove = docStorePermissions.includes("MOVE");
  const canCopy = docStorePermissions.includes("COPY");
  const canDownload = docStorePermissions.includes("DOWNLOAD_FILE");
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileName: "",
    fileUrl: "",
    extension: ""
  });
  const [renameState, setRenameState] = useState({
    nameWithoutExt: "",
    extension: "",
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target)
      ) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };

    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [contextMenu]);

  const handleContextMenuClick = (e, file) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    window.dispatchEvent(new Event("closeFolderContextMenu"));

    setContextMenu({
      visible: true,
      x: rect.left,
      y: rect.bottom + window.scrollY,
      file,
    });
  };

  useEffect(() => {
    const handler = () =>
      setContextMenu((prev) => ({ ...prev, visible: false }));
    window.addEventListener("closeFileContextMenu", handler);
    return () => window.removeEventListener("closeFileContextMenu", handler);
  }, []);

  const handleDelete = async () => {
    setShowConfirmPopup(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const isSystemLevel = activeTab !== "User Files";
      const queryParams = new URLSearchParams();
      const systemId = localStorage.getItem("selectedSystemId");

      if (isSystemLevel && systemId) {
        queryParams.append("systemId", systemId.toString());
        queryParams.append("isSystemLevel", "true");
      }

      const response = await axios.delete(
        `${baseUrl}/storage/file/${
          contextMenu.file.id
        }?${queryParams.toString()}`,
        { headers }
      );

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        setShowConfirmPopup(false);
        toast.success(
          `${contextMenu.file.name || "File"} deleted successfully`,
          {
            autoClose: 5000,
            closeButton: true,
            theme: "colored",
          }
        );
        setContextMenu({ ...contextMenu, visible: false });
        refresh?.();
        fetchStorageInfo();
      } else {
        toast.error(`Failed to delete ${contextMenu.file.name || "file"}`, {
          autoClose: 5000,
          closeButton: true,
          theme: "colored",
        });
      }
    } catch (err) {
      setShowConfirmPopup(false);
      toast.error(`Error deleting ${contextMenu.file.name || "file"}`, {
        autoClose: 5000,
        closeButton: true,
        theme: "colored",
      });
    }
  };

  const handleRename = (folder) => {
    const fullName = contextMenu?.file?.name || "";
    const lastDotIndex = fullName.lastIndexOf(".");

    let nameWithoutExt = fullName;
    let extension = "";

    if (lastDotIndex !== -1) {
      nameWithoutExt = fullName.substring(0, lastDotIndex);
      extension = fullName.substring(lastDotIndex); // includes the dot
    }

    // open modal with folder
    setModalState({
      isOpen: true,
      folder,
      newName: nameWithoutExt, // only base name in input
    });

    // also set rename state (base name + extension)
    setRenameState({
      nameWithoutExt,
      extension,
    });
  };

  // close without saving
  const handleCloseModal = () =>
    setModalState({ isOpen: false, folder: null, newName: "" });

  // keep input controlled
  const handleChange = (e) =>
    setRenameState((s) => ({ ...s, nameWithoutExt: e.target.value }));

  const onRenameClick = async () => {
    if (!renameState.nameWithoutExt || renameState.nameWithoutExt.trim() === "")
      return;

    const finalName = renameState.nameWithoutExt.trim() + renameState.extension;

    try {
      const response = await axios.put(
        `${baseUrl}/storage/file/${contextMenu.file.id}/rename`,
        { name: finalName },
        { headers }
      );

      if (response.status === 200) {
        setContextMenu({ ...contextMenu, visible: false });
        refresh?.();
        setShowSuccessPopup({
          type: "success",
          show: true,
          message: "File renamed successfully",
        });
      } else {
        setShowSuccessPopup({
          type: "warning",
          show: true,
          message: "Rename failed. Please try again",
        });
      }

      handleCloseModal();
    } catch (err) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Rename failed. Please try again",
      });
    }
  };

  const handleCopy = () => {
    startCopy({
      id: contextMenu.file.id,
      name: contextMenu.file.name,
      type: "file",
      docStoreType: activeTab, 
    });
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleMove = () => {
    startMove({
      id: contextMenu.file.id,
      name: contextMenu.file.name,
      type: "file",
      docStoreType: activeTab,
    });
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleView = () => {
    if (!contextMenu.file?.key || !accessToken) return;

    const { key, name, extension } = contextMenu.file;
    const encodedKey = encodeURIComponent(key);
    const fileExtension = name?.split(".").pop()?.toLowerCase();

    // Extensions to open in WOPI docx-viewer
    const supportedViewerExtensions = [
      "xls",
      "xlsx",
      "xlsm",
      "csv",
      "xlt",
      "xltx", // Excel
      "doc",
      "docx",
      "dot",
      "dotx",
      "rtf",
      "txt", // Word
      "ppt",
      "pptx",
      "pps",
      "ppsx",
      "pot",
      "potx", // PowerPoint
    ];

    const isSupportedForViewer =
      supportedViewerExtensions.includes(fileExtension);

    if (isSupportedForViewer) {
      window.open(`${wopiBaseUrl}/docx-viewer/${key}`, "_blank");
    } else {
      const viewUrl = `${baseUrl}/storage/file/download?key=${encodedKey}`;
      setPreviewModal({
        isOpen: true,
        fileName: `${name}.${extension}`,
        fileUrl: viewUrl,
        extension : extension
      });
    }

    setContextMenu({ ...contextMenu, visible: false });
  };

  if (!files || files.length === 0) return null;

  return (
    <>
      {viewMode === "table" ? (
        files.map((file) => (
          <FileRow
            key={file.id}
            icon={
              <img
                src={
                  file.type === "application/pdf"
                    ? userPdfImage
                    : file.type ===
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                      file.type === "application/msword"
                    ? userDocxImage
                    : file.type ===
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                      file.type === "application/vnd.ms-excel"
                    ? userExcelImage
                    : userFileImage
                }
                alt="folder"
                width="22"
                height="16"
              />
            }
            name={file.name.split(".")[0]}
            user={file?.username}
            modified={dateTimeFormatter(file.updatedAt)}
            extension={file?.type}
            size={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
            actions={
              <>
                <button
                  className="btn btn-sm"
                  onClick={(e) => handleContextMenuClick(e, file)}
                >
                  <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                </button>
              </>
            }
            onClick={() => {}}
          />
        ))
      ) : (
        <>
          {files.map((file) => (
            <div className="folder-list-card" key={file.id}>
              <FileCard
                icon={
                  <img
                    src={
                      file.type === "application/pdf"
                        ? userPdfImage
                        : file.type ===
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                          file.type === "application/msword"
                        ? userDocxImage
                        : file.type ===
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                          file.type === "application/vnd.ms-excel"
                        ? userExcelImage
                        : userFileImage
                    }
                    alt="folder"
                    width="40"
                    height="30"
                  />
                }
                name={file.name}
                user={file?.username}
                modified={dateTimeFormatter(file.updatedAt)}
                extension={file?.type}
                size={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                actions={
                  <>
                    <button
                      className="btn btn-sm"
                      onClick={(e) => handleContextMenuClick(e, file)}
                    >
                      <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                    </button>
                  </>
                }
                onClick={() => {}}
              />
            </div>
          ))}
        </>
      )}

      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="position-absolute theme-container border shadow-sm list-unstyled fnt14"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            padding: "0.5rem",
            minWidth: "120px",
            cursor: "pointer",
            ...(viewMode === "list" && { maxWidth: "40px" }),
          }}
        >
          <li
            className={`py-1 ps-1 dropdown-item ${
              activeTab !== "User Files" && !canView
                ? "text-muted disabled"
                : ""
            }`}
            onClick={
              activeTab === "User Files" || canView ? handleView : undefined
            }
          >
             <img
            src={viewIcon} 
            alt="View"
            width="14"
            height="14"
            className="context-menu-icon"
            />&nbsp;View
          </li>
          <li
            className={`py-1 ps-1 dropdown-item ${
              activeTab !== "User Files" && !canRename
                ? "text-muted disabled"
                : ""
            }`}
            onClick={
              activeTab === "User Files" || canRename ? handleRename : undefined
            }
          >
            <img
            src={renameIcon} 
            alt="Rename"
            width="14"
            height="17"
            className="context-menu-icon"
            />
           &nbsp;Rename
          </li>
          <li
            className={`py-1 ps-1 dropdown-item ${
              activeTab !== "User Files" && !canMove
                ? "text-muted disabled"
                : ""
            }`}
            onClick={
              activeTab === "User Files" || canMove ? handleMove : undefined
            }
          >
           <img
            src={moveIcon} 
            alt="move"
            width="14"
            height="14"
            className="context-menu-icon"
           />&nbsp;Move
          </li>
          <li
            className={`py-1 ps-1 dropdown-item ${
              activeTab !== "User Files" && !canCopy
                ? "text-muted disabled"
                : ""
            }`}
            onClick={
              activeTab === "User Files" || canCopy ? handleCopy : undefined
            }
          >
           <img
            src={copyIcon} 
            alt="Copy"
            width="14"
            height="14"
            className="context-menu-icon"
           />&nbsp;Copy
          </li>
          <li
            className={`py-1 ps-1 dropdown-item ${
              activeTab !== "User Files" && !canDownload
                ? "text-muted disabled"
                : ""
            }`}
            onClick={() => fetchAndDownloadFile(contextMenu.file)}
          >
            <i className="bi bi-download"></i>&nbsp;Download
          </li>
          <li
            className={`py-1 ps-1 dropdown-item text-danger ${
              activeTab !== "User Files" && !canDelete
                ? "text-muted disabled"
                : ""
            }`}
            onClick={
              activeTab === "User Files" || canDelete ? handleDelete : undefined
            }
          >
            <i className="bi bi-trash3"></i>&nbsp;Delete
          </li>
        </ul>
      )}

      <div>
        {modalState.isOpen && (
          <div
            className="modal fade modal-save-as show show-modal modal--opacity"
            id="docstore-rename-file-form"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="staticBackdropLabel"
            aria-hidden="true"
            style={{ display: "block" }}
          >
            <div className="modal-dialog" style={{ minWidth: "400px" }}>
              <div className="modal-content">
                <div className="modal-header d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    {/* Folder icon box */}
                    <div
                      style={{
                        border: "1px solid #f28c48",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                      }}
                    >
                      <i
                        className="bi bi-file-earmark-text"
                        style={{ fontSize: "1.5rem", color: "#1c88bf" }}
                      ></i>
                    </div>

                    {/* Title + Subtitle */}
                    <div>
                      <div className="fnt14 fw-600">Rename File</div>
                      <div className="fnt12" style={{ color: "#f28c48" }}>
                        All fields are mandatory *
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close px-1"
                    data-bs-dismiss="modal"
                    onClick={handleCloseModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control floatingInput"
                      id="renameFolder"
                      name="renameFolder"
                      value={renameState.nameWithoutExt || ""}
                      onChange={handleChange}
                      placeholder=""
                      autoFocus
                    />
                    <label className="floatingInput-label" htmlFor="customer">
                      Enter new file name
                    </label>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-bs-dismiss="modal"
                    onClick={onRenameClick}
                  >
                    Rename
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {previewModal.isOpen && (
          <FilePreviewDialog
            file={{
              fileName: previewModal.fileName,
              fileUrl: previewModal.fileUrl,
            }}
            onClose={() => setPreviewModal({
              isOpen: false,
              fileName: "",
              fileUrl: "",
              extension: ""
            })}
            docStorePermissions={docStorePermissions}
          />
        )}

        <SuccessPopup
          type={showSuccessPopup?.type}
          show={showSuccessPopup?.show}
          message={showSuccessPopup?.message}
          onClose={() => setShowSuccessPopup(false)}
        />

        <ConfirmPopup
          show={showConfirmPopup}
          message="Are you sure you want to delete this file?"
          onCancel={() => setShowConfirmPopup(false)}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </>
  );
};

export default FileList;
